import React, { useEffect, useState } from 'react';
import { Wrapper, MessageText } from './ReportedEventsList.styles';
import { useRotationStore } from '../../../context/RotationContext';
import { TankersEvent } from '../../../types/ViewModel/TankersEvent';
import { useEventStore } from '../../../context/EventContext';
import useEvent from '../../../app/useEvent';
import { useEventTypeStore } from '../../../context/EventTypeContext';
import { useAolStore } from '../../../context/AolContext';
import queryParamsUtils from '../../../utils/queryParamsUtils';
import { EventDate } from '../../../types/ViewModel/EventDate';
import { EventTypeData } from '../../../types/ViewModel/EventTypeData';
import { ReportLocationEnum } from '../../../enums/reportLocationEnum';
import useEventType from '../../../app/useEventType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import eventUtils from '../../../utils/event/eventUtils';
import { FrontendVersionEnum } from '../../../enums/frontendVersionEnum';
import useRotation from '../../../app/useRotation';
import featureUtils from '../../../utils/featureUtils';
import { Feature } from '../../../enums/featureEnum';
import { isDateSame } from 'odfjell-date-time-utils/dateTimeUtils';
import eventTypeGroupChangeLogUtils from '../../../utils/event/eventTypeGroupChangeLogUtils';

type Props = {
    sortAsc: boolean;
    showDetails: boolean;
};

const ReportedEventsList: React.FC<Props> = (props) => {
    const [nodeEventList, setNodeEventList] = useState<TankersEvent[]>([]);
    const [sortedReportedEventList, setSortedReportedEventList] = useState<TankersEvent[]>([]);
    const [lastDependentEventId, setLastDependentEventId] = useState<string | null>(null);
    const [lastNotValidatingEventId, setLastNotValidatingEventId] = useState<string | null>(null);
    const [delayInvalidEventIdList, setDelayInvalidEventIdList] = useState<string[]>([]);

    const { fullEventArray, selectedEvent, lastNotValidatingEvent, setLastNotValidatingEvent } = useEventStore();
    const { selectedTimelineItem } = useAolStore();
    const { eventTypeDataPort, eventTypeDataSea, setSelectedEventType, selectedEventType } = useEventTypeStore();
    const { updatedRotation, selectedNode } = useRotationStore();
    const {
        filterOnLocationContext,
        filterOnContextIncludeParentWithBerthCall,
        filterOnContextSingleLevelIncludingCargoEvents,
        eventHasDelayAndMissingInput,
        eventHasNoDelayAndExistingInput,
        eventHasDelayAndExistingInput,
        filterOnContext,
    } = useEvent();
    const { getPortCallFromNode } = useRotation();
    const { setQueryParamEvent } = queryParamsUtils();
    const { getNodeTypeFromEvent } = useEventType();
    const { getActivity, getUtcOffset, getEventDate } = eventUtils();
    const { isFeatureEnabled } = featureUtils();
    const { getRequiredPreviousTypeId } = eventTypeGroupChangeLogUtils();

    useEffect(() => {
        if (selectedNode && selectedEventType && selectedEventType.eventTypeGroup?.levelId === selectedNode.type) {
            const evGroupList = nodeEventList.filter(
                (ev: TankersEvent) =>
                    ev.eventType?.eventTypeGroupId === selectedEventType.eventTypeGroupId &&
                    ev.concurrentSequenceNumber === selectedEventType.parentSequenceNumber
            );
            if (evGroupList && evGroupList.length > 0) {
                if (evGroupList && evGroupList.length > 0) {
                    setLastDependentEventId(evGroupList[evGroupList.length - 1].eventId);
                } else {
                    setLastDependentEventId(null);
                }
            } else {
                // Check parents
                if (
                    getRequiredPreviousTypeId(selectedEventType.eventTypeGroup, selectedNode.firstTimeInPortCall) &&
                    selectedEventType.parentSequenceNumber
                ) {
                    const evGroupListParent = nodeEventList.filter(
                        (ev: TankersEvent) =>
                            ev.eventTypeId === getRequiredPreviousTypeId(selectedEventType.eventTypeGroup, selectedNode.firstTimeInPortCall) &&
                            ev.concurrentSequenceNumber === selectedEventType.parentSequenceNumber
                    );
                    if (evGroupListParent && evGroupListParent.length > 0) {
                        setLastDependentEventId(evGroupListParent[evGroupListParent.length - 1].eventId);
                    } else {
                        setLastDependentEventId(null);
                    }
                } else {
                    setLastDependentEventId(null);
                }
            }
            setLastNotValidatingEventId(null);
        } else {
            setLastDependentEventId(null);
        }
    }, [selectedEventType]);

    useEffect(() => {
        if (selectedNode && lastNotValidatingEvent && getNodeTypeFromEvent(lastNotValidatingEvent) === selectedNode.type) {
            setLastNotValidatingEventId(lastNotValidatingEvent.eventId);
        } else {
            setLastNotValidatingEventId(null);
        }
    }, [lastNotValidatingEvent]);

    useEffect(() => {
        if (selectedTimelineItem && fullEventArray && fullEventArray.length > 0) {
            const eventsList = filterOnLocationContext(
                fullEventArray.filter((event) => {
                    if (isDateSame(event?.eventDate?.actualDateTimeLocal, selectedTimelineItem.reportDate, 'day')) {
                        return 1;
                    }
                    return 0;
                }),
                selectedNode?.reportLocation
            );
            updateDelayStatusEventList(eventsList);
            setNodeEventList(eventsList);
        }
    }, [selectedTimelineItem]);

    useEffect(() => {
        // if (selectedNode && fullEventArray && fullEventArray.length > 0) {
        if (selectedNode && fullEventArray) {
            const eventsList = filterOnLocationContext(
                filterOnContextSingleLevelIncludingCargoEvents(selectedNode, fullEventArray),
                selectedNode.reportLocation
            );
            updateDelayStatusEventList(eventsList);
            setNodeEventList(eventsList);
        }
        setLastNotValidatingEventId(null);
    }, [selectedNode, fullEventArray]);

    useEffect(() => {
        if (nodeEventList && nodeEventList.length > 0) {
            if (props.sortAsc) {
                const sortedEventsList = sortEventList(nodeEventList, false);
                setSortedReportedEventList([...sortedEventsList]);
            } else {
                const sortedEventsList = sortEventList(nodeEventList, true);
                setSortedReportedEventList([...sortedEventsList]);
            }
        } else {
            setSortedReportedEventList([]);
        }
    }, [props.sortAsc, nodeEventList]);

    const updateDelayStatusEventList = (evList: TankersEvent[]) => {
        const delayStatusEvList: string[] = [];
        const portCall = getPortCallFromNode(selectedNode);
        const portCallEvList = filterOnContext(portCall, fullEventArray);
        const contextIncludeParentSpecificEventArray = filterOnContextIncludeParentWithBerthCall(selectedNode, fullEventArray);
        if (contextIncludeParentSpecificEventArray && contextIncludeParentSpecificEventArray.length > 0) {
            if (isFeatureEnabled(Feature.DelayReason, portCallEvList)) {
                evList.forEach((ev) => {
                    if (
                        eventHasDelayAndMissingInput(updatedRotation, ev, contextIncludeParentSpecificEventArray, selectedNode) ||
                        eventHasNoDelayAndExistingInput(updatedRotation, ev, contextIncludeParentSpecificEventArray, selectedNode)
                    )
                        delayStatusEvList.push(ev.eventId);
                });
            } else {
                evList.forEach((ev) => {
                    if (eventHasDelayAndExistingInput(ev)) delayStatusEvList.push(ev.eventId);
                });
            }
            setDelayInvalidEventIdList(delayStatusEvList);
        }
    };

    const getActualDateTime = (evDate: EventDate): Date | null => {
        if (evDate.actualDateTimeUtc) {
            return evDate.actualDateTimeUtc;
        }
        return null;
    };

    const sortEventList = (evList: TankersEvent[], isDescendingSort: boolean): TankersEvent[] => {
        return evList.sort((a: TankersEvent, b: TankersEvent) => {
            if (getActualDateTime(a.eventDate) && getActualDateTime(b.eventDate) && a.created && b.created) {
                if (
                    getActualDateTime(a.eventDate) > getActualDateTime(b.eventDate) ||
                    (isSameDateAndTime(getActualDateTime(a.eventDate), getActualDateTime(b.eventDate)) && a.created > b.created)
                ) {
                    return isDescendingSort ? 1 : -1;
                } else if (
                    getActualDateTime(a.eventDate) < getActualDateTime(b.eventDate) ||
                    (isSameDateAndTime(getActualDateTime(a.eventDate), getActualDateTime(b.eventDate)) && a.created < b.created)
                ) {
                    return isDescendingSort ? -1 : 1;
                } else {
                    return 0;
                }
            }
            return 0;
        });
    };

    const isSameDateAndTime = (aDate: Date, bDate: Date): boolean => {
        return (
            isDateSame(aDate, bDate, 'year') &&
            isDateSame(aDate, bDate, 'month') &&
            isDateSame(aDate, bDate, 'day') &&
            isDateSame(aDate, bDate, 'hour') &&
            isDateSame(aDate, bDate, 'minute') &&
            isDateSame(aDate, bDate, 'second')
        );
    };

    const getEventTypeData = (event: TankersEvent): EventTypeData => {
        return event.eventTypeContextId === ReportLocationEnum.Port ? eventTypeDataPort : eventTypeDataSea;
    };

    const handleItemClicked = (event: TankersEvent) => () => {
        if (event) {
            setLastNotValidatingEvent(null);
            setSelectedEventType(null);
            setQueryParamEvent(event.eventId?.toString());
            setLastDependentEventId(null);
        }
    };

    const displayArchiveIcon = (ev: TankersEvent) => {
        if (ev.frontendVersion === FrontendVersionEnum.Orca7) {
            return (
                <span className="event-archive-icon" title="This event has been created with ORCA 7.">
                    <FontAwesomeIcon icon="archive" />
                </span>
            );
        }
        return null;
    };

    const getIsSelectedCss = (eventId: string | null): string => {
        if (selectedEvent && eventId) return selectedEvent.eventId === eventId ? 'is-selected' : '';
        return '';
    };

    const getEvenIndex = (ind: number): string => {
        return ind % 2 ? 'alt-bg' : '';
    };

    const getLastDependentEventCss = (ev: TankersEvent | null): string => {
        if (lastDependentEventId && !lastNotValidatingEventId && ev) {
            if (lastDependentEventId === ev.eventId) {
                return 'is-last-dependent';
            }
        }
        return '';
    };

    const getLastNotValidatedEventCss = (eventId: string | null): string => {
        if (eventId) {
            if (delayInvalidEventIdList && delayInvalidEventIdList.length > 0) {
                const delayEvId = delayInvalidEventIdList.find((o) => o === eventId);
                if (delayEvId) {
                    return 'is-last-not-validating';
                }
            }
            if (lastNotValidatingEventId && eventId) {
                if (lastNotValidatingEventId === eventId) {
                    return 'is-last-not-validating';
                }
            }
        }
        return '';
    };

    return (
        <Wrapper>
            {selectedNode && sortedReportedEventList && sortedReportedEventList.length > 0 && fullEventArray && (
                <ul className="reported-events-list">
                    {sortedReportedEventList.map((ev, index) => {
                        return (
                            <li
                                key={ev.eventId}
                                onClick={handleItemClicked(ev)}
                                className={`
                                        ${getIsSelectedCss(ev.eventId)} 
                                        ${getEvenIndex(index)}
                                        ${getLastDependentEventCss(ev)}
                                        ${getLastNotValidatedEventCss(ev.eventId)}
                                    `}
                            >
                                <div title={getUtcOffset(ev.eventDate)} className="event-date">
                                    {getEventDate(ev.eventDate.actualDateTimeLocal)}
                                </div>
                                <div
                                    className="event-display-name"
                                    dangerouslySetInnerHTML={{
                                        __html: getActivity(
                                            updatedRotation,
                                            fullEventArray,
                                            selectedNode,
                                            getEventTypeData(ev),
                                            ev,
                                            props.showDetails
                                        ),
                                    }}
                                />
                                {displayArchiveIcon(ev)}
                            </li>
                        );
                    })}
                </ul>
            )}
            {(!sortedReportedEventList || sortedReportedEventList.length === 0) && (
                <MessageText>No registered events for selected period</MessageText>
            )}
        </Wrapper>
    );
};

export default ReportedEventsList;
